'use client';

/**
 * ErrorBoundary Component
 * Catches and handles React errors gracefully with fallback UI
 */

import { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import * as Sentry from '@sentry/nextjs';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  resetKeys?: Array<string | number>;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

/**
 * ErrorBoundary Class Component
 * React requires error boundaries to be class components
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      errorInfo,
    });

    Sentry.captureException(error, {
      tags: { errorBoundary: 'component' },
      extra: { componentStack: errorInfo.componentStack },
    });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: Props) {
    // Reset error boundary when resetKeys change
    if (this.state.hasError && this.props.resetKeys) {
      const hasChanges = this.props.resetKeys.some(
        (key, index) => key !== prevProps.resetKeys?.[index]
      );
      if (hasChanges) {
        this.reset();
      }
    }
  }

  reset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return <ErrorFallback error={this.state.error} reset={this.reset} />;
    }

    return this.props.children;
  }
}

/**
 * Default Error Fallback UI
 */
interface ErrorFallbackProps {
  error: Error | null;
  reset: () => void;
}

function ErrorFallback({ error, reset }: ErrorFallbackProps) {
  const isDev = process.env.NODE_ENV === 'development';

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <div className="w-full max-w-2xl">
        <div className="rounded-2xl border border-red-900/50 bg-slate-800/80 p-8 shadow-2xl backdrop-blur-sm">
          {/* Error Icon */}
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-red-500/10 p-4">
              <AlertTriangle className="h-12 w-12 text-red-500" />
            </div>
          </div>

          {/* Error Message */}
          <div className="text-center">
            <h1 className="mb-2 text-2xl font-bold text-white">Algo salió mal</h1>
            <p className="mb-6 text-slate-400">
              Ha ocurrido un error inesperado en la aplicación. Por favor, intenta recargar la
              página.
            </p>

            {/* Error Details (Dev Only) */}
            {isDev && error && (
              <div className="mb-6 rounded-lg bg-slate-900/50 p-4 text-left">
                <p className="mb-2 font-mono text-sm font-semibold text-red-400">
                  {error.name}: {error.message}
                </p>
                {error.stack && (
                  <pre className="overflow-x-auto text-xs text-slate-400">
                    <code>{error.stack}</code>
                  </pre>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
              <button
                onClick={reset}
                className="bg-brand-lime hover:bg-brand-lime/90 focus:ring-brand-lime/50 inline-flex items-center justify-center gap-2 rounded-lg px-6 py-3 font-semibold text-slate-900 transition-all duration-200 focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-800 focus:outline-none"
              >
                <RefreshCw className="h-5 w-5" />
                Reintentar
              </button>

              <button
                onClick={() => (window.location.href = '/')}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-6 py-3 font-semibold text-white transition-all duration-200 hover:bg-slate-700 focus:ring-2 focus:ring-slate-500/50 focus:ring-offset-2 focus:ring-offset-slate-800 focus:outline-none"
              >
                <Home className="h-5 w-5" />
                Ir al Inicio
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Higher-order component to wrap components with ErrorBoundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
}
